// src/components/games/edit/GameBindingEdit.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GameEditHeader from './GameEditHeader';
import GameEditMediaSlider from './GameEditMediaSlider';
import GameEditPurchaseBox from './GameEditPurchaseBox';
import GameEditDescription from './GameEditDescription';
import GameEditInfoBox from './GameEditInfoBox';
import GameEditLanguageSupport from './GameEditLanguageSupport';
import GameEditTag from './GameEditTag';
import GameEditRating from './GameEditRating';
import GameEditSystemRequirement from './GameEditSystemRequirement';
import GameEditLinks from './GameEditLinks';

export default function GameBindingEdit({ game = {}, onSubmit, gameId }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState(game.title || '');
  const [media, setMedia] = useState(game.media || []);
  const [price, setPrice] = useState(game.price != null ? String(game.price) : '');
  const [discountRate, setDiscountRate] = useState(
    game.discountRate != null ? String(game.discountRate) : ''
  );
  const [longDescription, setLongDescription] = useState(game.longDescription || '');
  const [shortDescription, setShortDescription] = useState(game.shortDescription || '');
  const [capsuleFiles, setCapsuleFiles] = useState([null, null]);
  const [capsulePreviews, setCapsulePreviews] = useState([
    game.capsuleImageUrl?.[0] || '',
    game.capsuleImageUrl?.[1] || '',
  ]);
  const [releaseDate, setReleaseDate] = useState(game.releaseDate || '');
  const [comingSoon, setComingSoon] = useState(!!game.comingSoon);
  const [genreInput, setGenreInput] = useState((game.genres || []).join(', '));
  const [tags, setTags] = useState(
    Array.isArray(game.tags) ? game.tags.join(', ') : typeof game.tags === 'string' ? game.tags : ''
  );
  const [developer, setDeveloper] = useState(game.developer || '');
  const [publisher, setPublisher] = useState(game.publisher || '');
  const [rating, setRating] = useState(game.rating || '');
  const [languages, setLanguages] = useState(game.languages || []);
  const [links, setLinks] = useState(game.links || {});
  const [systemRequirement, setSystemRequirement] = useState(
    game.systemRequirement || { minimum: {}, recommended: {} }
  );
  const [submitting, setSubmitting] = useState(false);

  const [purchaseLink, setPurchaseLink] = useState(game.links?.purchase || '');

  const handleInfoFileChange = file => {
    setCapsuleFiles(f => {
      const next = [...f];
      next[0] = file;
      return next;
    });
    setCapsulePreviews(p => {
      const next = [...p];
      next[0] = URL.createObjectURL(file);
      return next;
    });
  };

  const handlePurchaseFileChange = file => {
    setCapsuleFiles(f => {
      const next = [...f];
      next[1] = file;
      return next;
    });
    setCapsulePreviews(p => {
      const next = [...p];
      next[1] = URL.createObjectURL(file);
      return next;
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        id: gameId,
        title,
        price: Number(price) || 0,
        discountRate: Number(discountRate) || 0,
        longDescription,
        shortDescription,
        releaseDate,
        comingSoon,
        genres: genreInput
          .split(',')
          .map(g => g.trim())
          .filter(Boolean),
        tags: tags
          .split(',')
          .map(t => t.trim())
          .filter(Boolean),
        developer,
        publisher,
        rating,
        languages,
        links: { ...links, purchase: purchaseLink },
        systemRequirement,
        capsuleFiles,
        capsulePreviews,
        media,
      };
      await onSubmit(payload);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-[#121212] text-white min-h-screen">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <GameEditHeader value={title} onChange={setTitle} />
          <div className="space-x-2">
            <button
              type="button"
              onClick={() => navigate('/admin', { state: { tab: 'games' } })}
              className="px-7 py-2 bg-gray-600 rounded hover:bg-gray-500"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`px-7 py-2 rounded ${
                submitting ? 'bg-gray-500' : 'bg-blue-600 hover:bg-blue-500'
              }`}
            >
              {submitting ? '등록중…' : game.title ? '저장' : '등록'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <GameEditMediaSlider media={media} onChange={setMedia} />
            <GameEditPurchaseBox
              gameName={title}
              price={price}
              discountRate={discountRate}
              backgroundImage={capsulePreviews[1]}
              link={purchaseLink}
              onLinkChange={setPurchaseLink}
              onPriceChange={setPrice}
              onDiscountChange={setDiscountRate}
              onBackgroundChange={handlePurchaseFileChange}
            />
            <GameEditDescription
              description={longDescription}
              onChange={setLongDescription}
              gameId={gameId}
            />
          </div>

          <div className="space-y-4">
            <GameEditInfoBox
              gameId={gameId}
              capsuleImage={capsulePreviews[0]}
              onCapsuleChange={handleInfoFileChange}
              description={shortDescription}
              onDescriptionChange={setShortDescription}
              releaseDate={releaseDate}
              onReleaseDateChange={setReleaseDate}
              comingSoon={comingSoon}
              onComingSoonChange={setComingSoon}
              genre={genreInput}
              onGenreChange={setGenreInput}
              tags={tags}
              onTagsChange={setTags}
              developer={developer}
              onDeveloperChange={setDeveloper}
              publisher={publisher}
              onPublisherChange={setPublisher}
              rating={rating}
              onRatingChange={setRating}
            />
            <GameEditTag tags={tags} />
            <GameEditRating rating={rating} />
            <GameEditLanguageSupport value={languages} onChange={setLanguages} />
            <GameEditSystemRequirement
              requirements={systemRequirement}
              onChange={setSystemRequirement}
            />
            <GameEditLinks links={links} onChange={setLinks} />
          </div>
        </div>
      </div>
    </form>
  );
}
